import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { $dashboardExpenseCategoriesStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { ColorUtils, DateUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import { Select } from '@shared/widgets/select';
import { ColorsEnum, Icon, NoData, Text } from '@shared/widgets';
import { CATEGORY_ICONS } from './icons';

const dates = DateUtils.getLastMonths();

export const DashboardExpenseCategories = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardExpenseCategoriesStore = useUnit($dashboardExpenseCategoriesStore.store);

  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [date, setDate] = useState<string>(dates[0].value);

  useEffect(() => {
    if (currency) {
      $dashboardExpenseCategoriesStore.events.update({ currency, date });
    }
  }, [currency, date]);

  const symbol = useCurrencySymbol(currency);

  const colors = useMemo(() => {
    if (PaletteUtils.isVcfcPalette) return PaletteUtils.expenseCategoriesVcfcColors;
    return dashboardExpenseCategoriesStore.data.map(() => ColorUtils.getRandomColor());
  }, [dashboardExpenseCategoriesStore]);

  const options = useMemo<ApexOptions>(() => {
    return {
      chart: {
        id: 'expense',
        width: '100%',
        height: '100%',
        redrawOnParentResize: true,
        offsetX: 0,
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
          offsetX: 0,
          offsetY: 0,
          customScale: 1,
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 50,
          },
          donut: {
            size: '80%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontFamily: '"Proxima Nova", sans-serif',
                fontWeight: 600,
                color: 'var(--text-slate)',
                offsetY: 40, // позиция надписи "Total spend"
                formatter: function (val) {
                  const formatted = val.slice(0, 25);
                  if (val.length > 25) return formatted.concat('...');
                  return formatted;
                },
              },
              value: {
                show: true,
                fontSize: '34px',
                fontFamily: '"Prata", sans-serif',
                fontWeight: 400,
                color: 'var(--text-slate)',
                offsetY: 0,
                formatter: function (val) {
                  const format = FormatUtils.toMoneyFormat(parseFloat(val));
                  if (symbol) return symbol + format;
                  return format;
                },
              },
              total: {
                show: true,
                showAlways: false,
                label: 'Total spent',
                fontSize: '13px',
                width: 10,
                fontFamily: '"Proxima Nova", sans-serif',
                fontWeight: 400,
                color: 'var(--text-slate)',
                formatter: function (w) {
                  return symbol + FormatUtils.toMoneyFormat(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0));
                },
              },
            },
          },
        },
      },
      stroke: {
        // width: [4]
      },
      labels: dashboardExpenseCategoriesStore.data.map((ex) => `${ex.category}`),
      noData: {
        text: 'Loading...',
      },
      dataLabels: {
        enabled: false,
      },
      colors: colors,
      tooltip: {
        enabled: true,
        theme: 'dark',
        style: {
          fontSize: '14px',
          fontFamily: '"Proxima Nova", sans-serif',
        },
        x: {
          show: true,
          format: 'dd MMM',
          formatter: undefined,
        },
        y: {
          formatter: undefined,
          title: {
            formatter: (seriesName) => {
              if (symbol) return seriesName + ': ' + symbol;
              return seriesName;
            },
          },
        },
        marker: {
          show: true,
        },
      },
    };
  }, [dashboardExpenseCategoriesStore, symbol, colors]);
  const series = useMemo(() => dashboardExpenseCategoriesStore.data.map((ex) => ex.amount), [dashboardExpenseCategoriesStore, currency, date]);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex justify-between'>
        <Text weight='medium' size='2xl'>
          Expense categories
        </Text>
        <div className='flex gap-4'>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select
            wrapperClassName='min-w-40'
            items={dates.map((d) => ({ value: d.value, label: `${d.name} ${new Date(d.value).getFullYear()}` }))}
            value={date}
            onChange={(val) => setDate(val!)}
          />
        </div>
      </div>
      {series.length > 0 && options.labels?.length ? (
        <div className='flex justify-around items-center mt-16'>
          <div className='flex justify-around items-center'>
            <div className='flex'>
              <Chart options={options} series={series} type='donut' height={400} width='100%' />
            </div>
          </div>
          <div className='flex flex-col justify-center'>
            {dashboardExpenseCategoriesStore.data.map((ex, i) => {
              const icon = CATEGORY_ICONS[ex.category.replace(/[^a-zA-Z]/gi, '').toLowerCase() as keyof typeof CATEGORY_ICONS];
              return (
                <div key={i} className='flex my-1 items-center'>
                  <div className='flex justify-center items-center w-10 h-10 border rounded-sm bg-slate-gray' style={{ backgroundColor: colors[i] }}>
                    <Icon icon={icon} color='black' />
                  </div>
                  <Text className='ml-3 flex items-center text-center' weight='medium' color={ColorsEnum.Secondary}>
                    {ex.category}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <NoData />
      )}
    </div>
  );
};
